import { Chat } from '@/mocks/app/mockChatData';

/**
 * Filtros da seção "Automações" (dropdown de filtros de atendimento).
 *
 * "Campanha" é o nome do conceito na interface; no contrato com o servidor ele
 * continua sendo `Disparo` (`chat.disparo`, `idDisparo`, `CRIADO_POR_DISPARO`).
 * Por isso os nomes daqui falam campanha e as leituras do payload, não.
 *
 * A regra de visibilidade aqui é o INVERSO da dos outros filtros da lista. Nos
 * demais, "nada marcado" significa "mostre tudo". Aqui, um atendimento em
 * `CRIADO_POR_DISPARO` fica escondido por padrão: uma campanha para 5.000
 * contatos criaria 5.000 atendimentos que nunca responderam e que entupiriam a
 * lista, o Kanban e o contador de não lidos. Eles só aparecem quando o usuário
 * escolhe explicitamente uma campanha (ou "Todas") no seletor.
 */
export interface FiltrosAutomacoes {
    /** Campanha selecionada no seletor. Vazio = nenhuma campanha em foco. */
    idCampanha: string | null;
    /**
     * "Todas as campanhas": revela os atendimentos de QUALQUER campanha, sem
     * escolher uma. Mutuamente exclusivo com `idCampanha` — quem escreve nos dois
     * é o seletor, que nunca deixa os dois preenchidos.
     *
     * Campo próprio em vez de uma sentinela dentro de `idCampanha` de propósito:
     * sentinela em campo de id vaza como id de verdade na primeira vez que
     * alguém esquece de tratá-la.
     */
    todasCampanhas: boolean;
    /** Restringe aos atendimentos que a campanha CRIOU. Precisa de campanha em foco. */
    criadosPorCampanha: boolean;
    /** Restringe aos atendimentos que a campanha apenas TOCOU. Precisa de campanha em foco. */
    tocadosPorCampanha: boolean;
    /** Mostra somente atendimentos que passaram por IA (`idChatBotIa` preenchido). */
    iniciadoComIa: boolean;
    /** Mostra somente atendimentos que a IA considerou concluídos. */
    finalizadoPorIa: boolean;
}

export const filtrosAutomacoesVazios = (): FiltrosAutomacoes => ({
    idCampanha: null,
    todasCampanhas: false,
    criadosPorCampanha: false,
    tocadosPorCampanha: false,
    iniciadoComIa: false,
    finalizadoPorIa: false,
});

/**
 * Completa um objeto vindo do filtro persistido.
 *
 * `listFilters` é gravado em localStorage, então um usuário com filtro salvo tem
 * um `automacoes` **existente** e sem os campos novos — e existir é o bastante
 * para `filtrosAutomacoesVazios()` nunca ser chamado. Sem esta normalização os
 * `v-model` escreveriam em chaves ausentes e a leitura de `todasCampanhas`
 * devolveria `undefined`.
 */
export const normalizarFiltrosAutomacoes = (filtros: Partial<FiltrosAutomacoes> | null | undefined): FiltrosAutomacoes => ({
    ...filtrosAutomacoesVazios(),
    ...(filtros ?? {}),
});

/** Um atendimento criado por campanha e ainda sem resposta do contato. */
export function ehAtendimentoOcultoPorCampanha(chat: Pick<Chat, 'statusServidor'>): boolean {
    return chat.statusServidor === 'CRIADO_POR_DISPARO';
}

/**
 * Regra de visibilidade da seção Automações. Devolve `true` quando o chat deve
 * ser renderizado.
 *
 * Sem `filtros` (Kanban, contadores), vale só a regra base: atendimento oculto
 * por campanha não aparece, ponto.
 */
export function passaFiltroAutomacoes(chat: Chat, filtros?: FiltrosAutomacoes | null): boolean {
    const oculto = ehAtendimentoOcultoPorCampanha(chat);

    if (!filtros) return !oculto;

    const emFoco = filtros.idCampanha || filtros.todasCampanhas;

    if (emFoco) {
        // Com campanha em foco a lista vira a lista DAQUELA campanha (ou de todas):
        // tudo que não pertence a campanha alguma sai, inclusive atendimentos
        // normais. É o que dá sentido a revelar os ocultos — misturá-los com a fila
        // inteira não ajudaria ninguém.
        if (chat.isGroup) return false;
        if (!chat.disparo) return false;
        if (filtros.idCampanha && chat.disparo.idDisparo !== filtros.idCampanha) return false;

        const { criadosPorCampanha, tocadosPorCampanha } = filtros;
        // Nenhum dos dois marcado = os dois tipos. Marcar um restringe àquele tipo.
        if (criadosPorCampanha || tocadosPorCampanha) {
            const ehCriado = chat.disparo.creationStatus === 'CRIADO_POR_DISPARO';
            if (ehCriado && !criadosPorCampanha) return false;
            if (!ehCriado && !tocadosPorCampanha) return false;
        }
    } else if (oculto) {
        return false;
    }

    if (filtros.iniciadoComIa || filtros.finalizadoPorIa) {
        const passaIniciado = filtros.iniciadoComIa && !!chat.idChatBotIa;
        const passaFinalizado = filtros.finalizadoPorIa && chat.statusIntegracaoIa === 'FINALIZADO_POR_IA';
        if (!passaIniciado && !passaFinalizado) return false;
    }

    return true;
}

/** Há alguma campanha ou filtro de IA em foco? Usado para o badge de contagem. */
export function contarFiltrosAutomacoes(filtros: FiltrosAutomacoes): number {
    let total = 0;
    if (filtros.idCampanha) total++;
    if (filtros.todasCampanhas) total++;
    if (filtros.criadosPorCampanha) total++;
    if (filtros.tocadosPorCampanha) total++;
    if (filtros.iniciadoComIa) total++;
    if (filtros.finalizadoPorIa) total++;
    return total;
}

/** Descritor do badge de campanha exibido no ChatItem. Grupos nunca recebem badge. */
export function badgeCampanha(chat: Chat, nomeCampanha?: string | null): {
    titulo: string;
    classe: string;
    nome: string | null;
    criado: boolean;
} | null {
    if (chat.isGroup || !chat.disparo) return null;

    const criado = chat.disparo.creationStatus === 'CRIADO_POR_DISPARO';

    return {
        titulo: criado
            ? 'Atendimento aberto por uma campanha'
            : 'Atendimento que recebeu o conteúdo de uma campanha',
        classe: `campanha-badge campanha-badge-${criado ? 'criado' : 'tocado'}`,
        nome: nomeCampanha ?? null,
        criado,
    };
}
